import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe2, Copy, ExternalLink, CheckCircle2, AlertCircle, Loader2, Mail, MessageCircle, Eye, Link2, Sparkles } from "lucide-react";

/**
 * Phase 4 — Agenturportal-Card.
 * Zeigt:
 *  - Portal-Slug (editierbar)
 *  - Portal-Link (/portal/{slug})
 *  - Custom Portal Domain (editierbar) + DNS-Check
 *
 * Wichtig: NICHT mit der Mandanten-Angebotsdomain mischen.
 */
const STATUS_INFO = {
  not_configured: { label: "Nicht konfiguriert", color: "bg-slate-200 text-slate-700", note: "Optional: Hinterlegen Sie eine eigene Portal-Domain für Ihre Solarkunden." },
  pending_dns: { label: "DNS ausstehend", color: "bg-amber-100 text-amber-800", note: "DNS-Eintrag noch nicht gefunden. Bitte prüfen Sie Ihren CNAME-Eintrag." },
  verified: { label: "Verifiziert", color: "bg-emerald-100 text-emerald-800", note: "DNS bestätigt." },
  active: { label: "Aktiv", color: "bg-emerald-600 text-white", note: "Ihr Agenturportal ist über diese Domain aktiv." },
  error: { label: "Fehler", color: "bg-red-100 text-red-800", note: "DNS-Prüfung fehlgeschlagen. Bitte später erneut versuchen oder Domain prüfen." },
};

export default function AgencyPortalCard({ agency, onRefresh }) {
  const [slug, setSlug] = useState(agency?.agency_slug || "");
  const [savingSlug, setSavingSlug] = useState(false);
  const [domain, setDomain] = useState(agency?.custom_portal_domain || "");
  const [savingDomain, setSavingDomain] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dnsDetail, setDnsDetail] = useState("");
  const [activatingSsl, setActivatingSsl] = useState(false);
  const [sslPollActive, setSslPollActive] = useState(false);

  // Sprint 8b — Polling bei ssl_status === 'pending'
  useEffect(() => {
    const isPending = agency?.ssl_status === "pending" || agency?.ssl_activation_in_progress;
    let cancelled = false;
    setTimeout(() => {
      if (!cancelled) setSslPollActive(!!isPending);
    }, 0);
    if (!isPending) return () => { cancelled = true; };
    const tick = () => { if (!cancelled && onRefresh) onRefresh(); };
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [agency?.ssl_status, agency?.ssl_activation_in_progress, onRefresh]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setSlug(agency?.agency_slug || "");
      setDomain(agency?.custom_portal_domain || "");
    });
    return () => { cancelled = true; };
  }, [agency]);

  const status = agency?.custom_portal_domain_status || "not_configured";
  const dnsTarget = agency?.custom_portal_domain_dns_target || "portal.revenioo.ai";
  const portalPath = `/portal/${slug || "ihr-slug"}`;
  const portalUrl = typeof window !== "undefined" ? `${window.location.origin}${portalPath}` : portalPath;

  const sanitizedSlug = (v) => (v || "").toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-");

  const saveSlug = async () => {
    const s = sanitizedSlug(slug);
    if (!s) { toast.error("Slug fehlt"); return; }
    if (s === agency?.agency_slug) { toast.message("Keine Änderung."); return; }
    setSavingSlug(true);
    try {
      await api.patch("/agency/profile", { agency_slug: s });
      toast.success("Portal-Slug aktualisiert");
      onRefresh && onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Speichern fehlgeschlagen");
    } finally { setSavingSlug(false); }
  };

  const saveDomain = async () => {
    const d = (domain || "").trim().toLowerCase();
    setSavingDomain(true);
    try {
      await api.patch("/agency/profile", { custom_portal_domain: d });
      toast.success(d ? "Portal-Domain gespeichert" : "Portal-Domain entfernt");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally { setSavingDomain(false); }
  };

  const checkDns = async () => {
    setChecking(true);
    setDnsDetail("");
    try {
      const r = await api.post("/agency/portal-domain/check");
      setDnsDetail(r.data.detail || "");
      if (r.data.status === "active") toast.success("Portal-Domain ist aktiv!");
      else toast.message("DNS-Status: " + (r.data.status || "?"));
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("DNS-Prüfung fehlgeschlagen");
    } finally { setChecking(false); }
  };

  const activateSsl = async () => {
    setActivatingSsl(true);
    try {
      const r = await api.post("/agency/portal-domain/activate-ssl");
      if (r.data?.ok) toast.success("HTTPS aktiviert!");
      else toast.error(r.data?.error || "SSL-Aktivierung fehlgeschlagen");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SSL-Aktivierung fehlgeschlagen");
    } finally { setActivatingSsl(false); }
  };

  const copy = async (text) => {
    const ok = await copyText(text);
    if (ok) toast.success("Kopiert");
    else toast.error("Kopieren fehlgeschlagen — bitte manuell markieren");
  };

  const info = STATUS_INFO[status] || STATUS_INFO.not_configured;

  // Share-Texte für Email + WhatsApp
  const shareSubject = `Ihr Solar Sales Portal von ${agency?.name || ""}`;
  const shareBody = `Hallo,\n\nüber dieses Portal verwalten Sie ab sofort gemeinsam mit uns Ihre PV-Leads, Angebote und Nachfass-Sequenzen:\n\n${portalUrl}\n\nVielen Dank,\n${agency?.name || "Ihre Agentur"}`;
  const mailtoHref = `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareBody)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareBody)}`;

  const views = Number(agency?.portal_views_total || 0);
  const lastViewed = agency?.portal_last_viewed_at
    ? new Date(agency.portal_last_viewed_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })
    : null;

  return (
    <Card className="border-violet-200 p-6 space-y-5" data-testid="agency-portal-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
          <Globe2 className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Agenturportal</h2>
          <p className="text-xs text-slate-500">Login- und Portaladresse für Ihre Solarkunden.</p>
        </div>
      </div>

      {/* === Magic Share Link (Phase 4.1) === */}
      <div
        className="rounded-xl p-5 relative overflow-hidden border"
        style={{
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          borderColor: "#7c3aed",
        }}
        data-testid="portal-share-card"
      >
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,1) 0%, transparent 50%)" }} />
        <div className="relative z-10 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold">Ihr Magic Link</span>
            </div>
            <div className="flex items-center gap-3 text-white/90 text-xs" data-testid="portal-stats">
              <span className="inline-flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                <span className="font-bold tabular-nums" data-testid="portal-views-count">{views}</span>
                <span className="text-white/60">Aufrufe</span>
              </span>
              {lastViewed && (
                <span className="text-white/55 hidden sm:inline">· zuletzt {lastViewed}</span>
              )}
            </div>
          </div>

          <p className="text-white/80 text-sm leading-snug">
            Teilen Sie diesen Link mit Ihren Solar-Kunden — er führt direkt zum gebrandeten Login.
          </p>

          <div className="bg-white/95 rounded-lg p-2.5 flex items-center gap-2 flex-wrap">
            <Link2 className="w-4 h-4 text-violet-700 ml-1 shrink-0" />
            <code className="font-mono text-xs text-slate-800 flex-1 min-w-0 truncate" data-testid="portal-share-url">
              {portalUrl}
            </code>
            <Button
              size="sm"
              onClick={() => copy(portalUrl)}
              className="bg-violet-700 hover:bg-violet-800 text-white h-8"
              data-testid="portal-share-copy"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Kopieren
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href={mailtoHref}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-medium border border-white/20 transition-colors"
              data-testid="portal-share-email"
            >
              <Mail className="w-3.5 h-3.5" /> Per E-Mail
            </a>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-medium border border-white/20 transition-colors"
              data-testid="portal-share-whatsapp"
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
            <a
              href={portalPath}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-medium border border-white/20 transition-colors"
              data-testid="portal-share-open"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Vorschau
            </a>
          </div>
        </div>
      </div>

      {/* === Slug (kompakt) === */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700">Portal-Slug</Label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400 font-mono">/portal/</span>
          <Input
            data-testid="portal-slug-input"
            value={slug}
            onChange={(e) => setSlug(sanitizedSlug(e.target.value))}
            placeholder="ihre-agentur"
            className="font-mono flex-1 min-w-[200px]"
          />
          <Button
            data-testid="portal-slug-save"
            onClick={saveSlug}
            disabled={savingSlug || !slug || slug === agency?.agency_slug}
            size="sm"
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {savingSlug ? "Speichern …" : "Slug speichern"}
          </Button>
        </div>
        <div className="bg-violet-50 border border-violet-100 rounded-md px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">Portal-Pfad:</span>
          <code className="font-mono text-violet-700 text-xs" data-testid="portal-link-display">{portalPath}</code>
        </div>
      </div>

      <div className="border-t border-slate-100" />

      {/* === Custom Portal Domain === */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <Label className="text-sm font-medium text-slate-700">Agenturportal-Domain (optional)</Label>
            <p className="text-xs text-slate-500 mt-0.5">Eigene Domain für Login und Portal Ihrer Solarkunden.</p>
          </div>
          <Badge className={info.color}>
            {status === "active" ? <CheckCircle2 className="w-3 h-3 mr-1" /> : status === "error" ? <AlertCircle className="w-3 h-3 mr-1" /> : null}
            {info.label}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            data-testid="portal-domain-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value.toLowerCase())}
            placeholder="portal.ihre-agentur.de"
            className="font-mono flex-1 min-w-[220px]"
          />
          <Button
            data-testid="portal-domain-save"
            onClick={saveDomain}
            disabled={savingDomain || (domain || "").toLowerCase() === (agency?.custom_portal_domain || "").toLowerCase()}
            size="sm"
            variant="outline"
          >
            {savingDomain ? "Speichern …" : "Speichern"}
          </Button>
        </div>

        {(domain || agency?.custom_portal_domain) ? (
          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2 border border-slate-200">
            <p className="font-semibold text-slate-700 text-xs uppercase tracking-wider">DNS-Anweisung</p>
            <p className="text-slate-600 text-xs">Setzen Sie bei Ihrem DNS-Provider folgenden <span className="font-mono font-semibold">CNAME</span>-Eintrag:</p>
            <pre className="bg-white border border-slate-200 rounded px-3 py-2 text-xs overflow-x-auto">
              <span className="text-slate-500">CNAME</span>  {domain || agency?.custom_portal_domain}  →  {dnsTarget}
            </pre>
            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="text-xs text-slate-500">{info.note}</span>
              <Button
                data-testid="portal-dns-check-btn"
                onClick={checkDns}
                disabled={checking}
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {checking ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Prüfe …</>) : "DNS prüfen"}
              </Button>
            </div>
            {dnsDetail && (
              <p data-testid="portal-dns-detail" className={`text-xs ${status === "active" ? "text-emerald-700" : status === "error" ? "text-red-700" : "text-slate-600"}`}>
                {dnsDetail}
              </p>
            )}
            {status === "active" && agency?.custom_portal_domain && (
              <p className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> DNS aktiv: <a href={`https://${agency.custom_portal_domain}`} target="_blank" rel="noreferrer" className="underline">https://{agency.custom_portal_domain}</a>
              </p>
            )}
            <SslStatusRow
              sslStatus={agency?.ssl_status}
              lastError={agency?.ssl_error_message || agency?.last_error}
              domainStatus={status}
              onActivate={activateSsl}
              activating={activatingSsl}
              testidPrefix="portal"
            />
          </div>
        ) : (
          <p className="text-xs text-slate-400">{info.note}</p>
        )}
      </div>

      <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-3">
        Hinweis: Die Mandanten-<b>Angebotsdomain</b> (für öffentliche Angebotsseiten Ihrer Solarfirmen) wird separat im jeweiligen Mandanten konfiguriert.
      </p>
    </Card>
  );
}

/**
 * Sprint 8b — SSL-Status-Zeile mit Activate-Button + Retry.
 */
function SslStatusRow({ sslStatus, lastError, domainStatus, onActivate, activating, testidPrefix }) {
  // Nur sichtbar, wenn DNS mindestens verified ist
  if (!domainStatus || domainStatus === "not_configured" || domainStatus === "pending_dns") return null;
  const s = sslStatus || "not_started";

  if (s === "active") {
    return (
      <p data-testid={`${testidPrefix}-ssl-status`} className="text-xs flex items-center text-emerald-700 font-medium gap-1">
        <CheckCircle2 className="w-3 h-3" /> HTTPS aktiv
      </p>
    );
  }
  if (s === "pending") {
    return (
      <p data-testid={`${testidPrefix}-ssl-status`} className="text-xs flex items-center text-amber-700 font-medium gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> SSL wird eingerichtet (ca. 30–90 s) …
      </p>
    );
  }
  // not_started oder error → Activate-Button anzeigen, falls DNS verified
  const canActivate = domainStatus === "verified" || domainStatus === "active";
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p data-testid={`${testidPrefix}-ssl-status`} className={`text-xs flex items-center gap-1 font-medium ${s === "error" ? "text-red-700" : "text-slate-600"}`}>
          {s === "error" ? <AlertCircle className="w-3 h-3" /> : null}
          {s === "error"
            ? "SSL-Fehler"
            : domainStatus === "verified"
            ? "DNS verifiziert — SSL noch nicht aktiviert"
            : "SSL noch nicht aktiviert"}
        </p>
        {canActivate && (
          <Button
            data-testid={`${testidPrefix}-activate-ssl-btn`}
            onClick={onActivate}
            disabled={activating}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
          >
            {activating ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Aktiviere …</>) : (s === "error" ? "Erneut versuchen" : "SSL aktivieren")}
          </Button>
        )}
      </div>
      {s === "error" && lastError && (
        <p data-testid={`${testidPrefix}-ssl-error`} className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 break-words">
          {lastError}
        </p>
      )}
    </div>
  );
}
